'use client'

import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Heart, X, ShoppingCart, ArrowLeft } from 'lucide-react'
import { useWishlist } from '../context/WishlistContext'
import { useCart } from '../context/CartContext'
import { useAuth, api } from '../context/AuthContext'
import { toast } from 'sonner'

const Wishlist = () => {
  const navigate = useNavigate()
  const { wishlist, refreshWishlist, removeFromWishlist } = useWishlist()
  const { refreshCartCount } = useCart()
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/')
      return
    }
    refreshWishlist()
  }, [isAuthenticated, navigate, refreshWishlist])

  const addToCart = async (product) => {
    // Check if product requires color/size selection
    const hasColors = product.colors && product.colors.length > 0
    const hasSizes = product.sizes && product.sizes.length > 0
    const hasVariantStock = product.variant_stock && product.variant_stock.length > 0
    
    // If product has variants, navigate to product details instead of adding directly
    if (hasColors || hasSizes || hasVariantStock) {
      navigate(`/products/${product.id}`)
      return
    }

    try {
      await api.post('/cart/add', {
        product_id: product.id,
        quantity: 1
      })
      refreshCartCount()
      const variantInfo = [product.color, product.size].filter(Boolean).join(', ');
      toast.success(`${product.name}${variantInfo ? ` (${variantInfo})` : ''} added to cart!`)
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to add to cart';
      
      // Don't show stock error toast if product has variants - user needs to select different variant
      if (hasColors || hasSizes || hasVariantStock) {
        if (errorMessage.toLowerCase().includes('stock') || errorMessage.toLowerCase().includes('out of stock')) {
          toast.error('Please select a different variant');
          return;
        }
      }
      
      toast.error(errorMessage);
    }
  }

  const handleRemove = async (productId) => {
    const success = await removeFromWishlist(productId)
    if (success) {
      toast.success('Removed from wishlist')
    }
  }

  const getProductImage = (product) => {
    // If the item has color_images and colors, use the same logic as home page
    if (product.color_images && product.colors?.length > 0) {
      const firstColor = product.colors[0];
      
      // Try exact match first
      if (product.color_images[firstColor]?.[0]) {
        return product.color_images[firstColor][0];
      }
      
      // Try case-insensitive match
      const normalizedSearch = firstColor.trim().toLowerCase();
      const matchingKey = Object.keys(product.color_images).find(
        key => key.trim().toLowerCase() === normalizedSearch
      );
      
      if (matchingKey && product.color_images[matchingKey]?.[0]) {
        return product.color_images[matchingKey][0];
      }
      
      // If no match for first color, try any available color
      const firstAvailableColorImages = Object.values(product.color_images).find(images => images?.length > 0);
      if (firstAvailableColorImages?.[0]) {
        return firstAvailableColorImages[0];
      }
    }
    
    // Fallback to regular images
    if (Array.isArray(product.images) && product.images.length > 0) return product.images[0];
    if (typeof product.image === "string") return product.image;
    return "/placeholder.png";
  }

  if (wishlist.length === 0) {
    return (
      <div className="min-h-screen bg-[#030804] pt-16 px-4">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-8 transition"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>

          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-zinc-900 flex items-center justify-center">
              <Heart size={48} className="text-zinc-700" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Your wishlist is empty</h2>
            <p className="text-zinc-400 mb-8">Start adding products you love!</p>
            <button
              onClick={() => navigate('/products')}
              className="px-8 py-3 bg-[#ffcc32] text-black font-bold rounded-lg hover:bg-[#ffda5c] transition"
            >
              Browse Products
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#030804] pt-16 px-4 pb-16">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/70 hover:text-white mb-8 transition"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">My Wishlist</h1>
          <p className="text-zinc-400">{wishlist.length} items</p>
        </div>

        {/* Horizontal Scroll Container */}
        <div className="overflow-x-auto pb-4 pr-[calc(16%+24px)] sm:pr-[calc(18%+24px)] scrollbar-hide">
          <div className="flex gap-6" style={{ width: 'max-content' }}>
            {wishlist.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-orange-500/50 transition cursor-pointer"
                style={{ width: '300px', flexShrink: 0 }}
                onClick={() => navigate(`/product/${item.product_id || item.id}`)}
              >
                <div className="relative">
                  <img
                    src={getProductImage(item.product?.images || item.images)}
                    alt={item.product?.name || item.name}
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemove(item.product_id || item.id)
                    }}
                    className="absolute top-3 right-3 w-8 h-8 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="p-4">
                  <h3 className="text-white font-semibold mb-2 line-clamp-2">
                    {item.product?.name || item.name}
                  </h3>
                  <p className="text-orange-500 font-bold text-lg mb-4">
                    Rs. {(item.product?.price || item.price || 0).toLocaleString()}
                  </p>

                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      addToCart(item.product || item)
                    }}
                    className="w-full py-3 bg-[#ffcc32] text-black font-bold rounded-lg hover:bg-[#ffda5c] transition flex items-center justify-center gap-2"
                  >
                    <ShoppingCart size={18} />
                    <span>Add to Cart</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Wishlist
